/**
 * The board store: the one funnel through which server state enters the app.
 *
 * The load-bearing idea is `visibleRows` — a single flattened array that BOTH
 * the tree pane and the gantt pane render. Row n in one is row n in the other
 * by construction, so the two panes cannot drift out of alignment and they
 * share a scroll container.
 */
import { computed, reactive, ref, toRaw, watch } from "vue";
import { defineStore } from "pinia";
import { api } from "../lib/api.js";
import { connect } from "../lib/socket.js";
import { toast } from "../lib/toast.js";
import { gitPushed } from "../lib/gitPushes.js";
import { gitGraphPushed } from "../lib/gitGraphs.js";
import { runEnded } from "../lib/runEnds.js";
import { deployStatusChanged } from "../lib/deploys.js";
import { automationReconnected, flowRemoved, flowUpserted, logsArrived, signedOutAutomation, statusesChanged, widgetChanged } from "../lib/automation.js";
import { claudeCalendarChanged } from "../lib/claudeStatus.js";
import { checkBuild, setDeploying } from "../lib/deployReload.js";
import { loadTaskPromptTemplate } from "../lib/taskPromptTemplate.js";
import { forgetQueuedTasks, loadQueuedTasks, queuedMoved } from "../lib/claudeQueued.js";
import { VIEWS, compareRepos, moveRepo, type SwitcherView } from "@shared/settings.js";
import { rankBetween } from "@shared/rank.js";
import { computeRollups, childrenByParent, dominantValue, keepWithAncestors, pruneDone, type Rollup } from "@shared/rollup.js";
import { useViewport } from "./viewport.js";
import { useFinance } from "./finance.js";
import { useShopping } from "./shopping.js";
import type { UserSettings } from "@shared/settings.js";
import { DEFAULT_EST_HOURS, DONE_STATUSES, URGENCY_DEFAULT, inheritedField, type Availability, type AvailabilityInput, type ClientMsg, type EditableField, type FieldLock, type Message, type Presence, type ServerEvent, type Status, type Task, type TaskEvent, type Team, type User } from "@shared/types.js";

export interface TaskRowData {
  kind: "task";
  key: string;
  task: Task;
  depth: number;
  rollup: Rollup;
  hasChildren: boolean;
  expanded: boolean;
}

/** A synthetic heading row. It carries a Rollup like any other row so the gantt
 *  can draw the group's span without knowing groups exist. */
export interface GroupRowData {
  kind: "group";
  key: string;
  label: string;
  /** Unfinished tasks anywhere under this heading, for the count in the tree. */
  count: number;
  /** Identity colour when the heading is a person; null for a repo. This board
   *  identifies people by colour everywhere else, and a name alone in a list of
   *  names is harder to pick out than the avatar beside it. */
  color: string | null;
  rollup: Rollup;
  expanded: boolean;
  /** Indent: 0 for a top-level heading, 1 for one filed inside another. */
  depth: number;
  /** The repo a repo heading files (null for "no repo"). */
  repo?: string | null;
  /** A heading on the Personal board: its "+" makes a personal task. */
  personal?: boolean;
}

/** "+ Add task" at the foot of a repo group. A row of its own, not a button
 *  tacked under the tree, so the gantt gives it a blank line and every bar
 *  below still sits level with its task. */
export interface AddRowData {
  kind: "add";
  key: string;
  /** What the new task is filed under; null for the "no repo" group. */
  repo: string | null;
  /** Adds a personal task — an add row on the Personal board. */
  personal: boolean;
  /** Indent, matching the group it sits under; 0 when it stands on its own. */
  depth: number;
  rollup: Rollup;
}

export type Row = TaskRowData | GroupRowData | AddRowData;

const EMPTY_ROLLUP: Rollup = {
  percent: 0, doneWeight: 0, totalWeight: 0, leafCount: 0,
  startMs: null, endMs: null, overflows: false, spentHours: 0,
  estHours: null, descendantCount: 0, openDescendants: 0, urgency: 0,
};

/**
 * Guarantee somewhere to click to add a task.
 *
 * Every repo heading carries one, which covers the usual case, but an empty
 * board has no headings. Since the topbar no longer carries a "+ Task" button, a list with no
 * add row anywhere would be a board you cannot add to at all.
 */
function withAddRow(rows: Row[]): Row[] {
  if (rows.some((r) => r.kind === "add")) return rows;
  return [...rows, { kind: "add", key: "add:end", repo: null, personal: false, depth: 0, rollup: EMPTY_ROLLUP }];
}

/** Group keys carry a colon and task ids never do, which is how `toggle` tells
 *  one from the other. */
const groupKey = (mode: string, value: string | null): string => `${mode}:${value ?? ""}`;
const personalGroupKey = (repo: string | null): string => `personal-repo:${repo ?? ""}`;
const isGroupKey = (key: string): boolean => key.includes(":");

/**
 * The child map with every sibling list reordered most-urgent first, by rolled-up
 * urgency so a parent rises with its most pressing sub-task. Array sort is
 * stable, so equal urgency keeps the hand-dragged rank order.
 */
function sortByUrgency(childMap: Map<string | null, Task[]>, rollupMap: Map<string, Rollup>): Map<string | null, Task[]> {
  const urgency = (t: Task): number => rollupMap.get(t.id)?.urgency ?? 0;
  const out = new Map<string | null, Task[]>();
  for (const [parent, list] of childMap) out.set(parent, [...list].sort((a, b) => urgency(b) - urgency(a)));
  return out;
}

/** Roll several roots up into one heading. Same accumulation a parent does over
 *  its children — a group is just a parent without a task behind it. */
function combineRollups(members: Rollup[]): Rollup {
  let doneWeight = 0;
  let totalWeight = 0;
  let leafCount = 0;
  let descendantCount = 0;
  let openDescendants = 0;
  let startMs: number | null = null;
  let endMs: number | null = null;
  let estHours: number | null = null;
  let spentHours = 0;
  let urgency = 0;

  for (const r of members) {
    doneWeight += r.doneWeight;
    totalWeight += r.totalWeight;
    leafCount += r.leafCount;
    descendantCount += r.descendantCount + 1;
    openDescendants += r.openDescendants;
    if (r.startMs !== null) startMs = startMs === null ? r.startMs : Math.min(startMs, r.startMs);
    if (r.endMs !== null) endMs = endMs === null ? r.endMs : Math.max(endMs, r.endMs);
    if (r.estHours !== null) estHours = (estHours ?? 0) + r.estHours;
    spentHours += r.spentHours;
    urgency = Math.max(urgency, r.urgency);
  }

  return {
    percent: totalWeight > 0 ? doneWeight / totalWeight : 0,
    doneWeight, totalWeight, leafCount, startMs, endMs,
    overflows: false, spentHours, estHours, descendantCount, openDescendants, urgency,
  };
}

/** What `viewport.team` holds while the reader's own tasks are on screen. */
export const PERSONAL = "personal";

/**
 * The id of the task being added, before it exists.
 *
 * "+ Add task" used to POST a task called "New task" and then let you rename
 * it, so every abandoned click left a row on everybody's board — broadcast,
 * notified, in the widget. Instead the row is a DRAFT: a local task under this
 * id, in `tasks` like any other so the tree, the gantt and the drawer need no
 * special case, which nothing has been sent about. The first save — giving it
 * a title, which the server requires anyway — is what creates it.
 *
 * Not a colon key: `toggle` tells a group heading from a task by the colon,
 * and real ids never contain one either.
 */
export const DRAFT_ID = "draft";

/** What a draft still needs from the caller once it is saved: the placement
 *  the server cannot work out from the task itself. Its own fields (title,
 *  repo, urgency…) are read off the draft row at that point, so anything
 *  changed while it sat there goes with it. */
interface DraftPlacement {
  parentId?: string | null;
  afterId?: string | null;
  personal?: boolean;
  teamId?: string | null;
}

export type ConnStatus = "connecting" | "open" | "offline";

/** The most a reconnect's resync reads wait, spread at random (see `onOpen`). */
const RESYNC_JITTER_MS = 2000;

export const useBoard = defineStore("board", () => {
  const tasks = reactive(new Map<string, Task>());
  const users = reactive(new Map<string, User>());
  /** The reader's teams, by name. The server sends every one of their tasks;
   *  the board, messages and calendar show one team at a time. */
  const teams = ref<Team[]>([]);
  const timelines = reactive(new Map<string, TaskEvent[]>());
  const presence = ref<Presence[]>([]);
  /**
   * What the reader has opened, and what they have folded shut — opposite
   * polarities, deliberately.
   *
   * A task starts CLOSED. A board of any size is mostly other people's
   * sub-tasks, and opening it fully expanded means the first thing anybody sees
   * is a wall of rows they have to fold away before they can read the shape of
   * the work. So `openTasks` holds what was opened, and an empty set is a board
   * showing its top level.
   *
   * A repo heading starts CLOSED too, and `openGroups` holds what was opened:
   * the user asked for the board to open as its list of repos. `reveal()`
   * opens the heading a task is filed under, so a deep link still lands.
   */
  const openTasks = reactive(new Set<string>());
  const openGroups = reactive(new Set<string>());

  const me = ref<User | null>(null);
  const lastSeq = ref(0);
  const status = ref<ConnStatus>("connecting");
  const selectedId = ref<string | null>(null);
  const loaded = ref(false);

  // ── message board ──────────────────────────────────────────────────────
  /** The newest page of the board, plus any older pages scrolled back to. Oldest first. */
  const messages = ref<Message[]>([]);
  /** Whether anything older than messages[0] exists on the server. */
  const olderMessages = ref(false);
  /** This person's read marker, as the server last told us. */
  const messagesReadAt = ref(0);
  const messagesLoaded = ref(false);

  // ── availability ───────────────────────────────────────────────────────
  /** Every entry fetched so far, by id, kept live by `availability*` events.
   *  Not windowed here — the calendar filters to the week it is showing, and
   *  holding a few weeks either side is what makes paging back instant. */
  const availability = reactive(new Map<string, Availability>());
  /** The widest window fetched, so flicking between two weeks refetches nothing. */
  const availabilityWindow = ref<{ startMs: number; endMs: number } | null>(null);

  /** The field this tab is typing in, if any. Mirrors what we told the server. */
  const myLock = ref<FieldLock | null>(null);

  /**
   * This tab's changes the server has not answered yet, keyed by op: writes in
   * flight, and previews (a bar or slider mid-drag) that nothing has been sent
   * for. Each is drawn over whatever the server says meanwhile, so somebody
   * else's change to the same task never snaps alice's value back under her.
   */
  const pending = new Map<string, { id: string; patch: Partial<Task> }>();
  /** The server's latest row for each task with something pending, which is
   *  what the pending patches are drawn over, and what is left once they go. */
  const confirmed = new Map<string, Task>();

  /** The task being added, while nothing about it has been sent. Null when
   *  there is none — there is at most one, since starting another is saying
   *  you were done with the first. `saving` from the moment its create is in
   *  flight: it is the server's now, and closing the drawer must not drop it. */
  const draft = ref<{ placement: DraftPlacement; saving: boolean } | null>(null);

  let socket: ReturnType<typeof connect> | null = null;

  // Grouping is a view preference, so it lives in the viewport store; the rows
  // it shapes are built here, which is the one place both panes read from.
  const viewport = useViewport();
  const finance = useFinance();
  const shopping = useShopping();

  // ── teams ──────────────────────────────────────────────────────────────
  /**
   * The reader's own tasks are picked like a team: the team picker lists
   * Personal alongside the teams, and the board shows one or the other, never
   * both. Personal has no thread and no calendar — those are a team's.
   */
  const personalView = computed(() => viewport.team === PERSONAL || teams.value.length === 0);
  /** The team on screen: the saved one while the reader is still in it, else
   *  their first. Null on Personal, and for someone in no team. */
  const teamId = computed((): string | null =>
    personalView.value
      ? null
      : teams.value.some((t) => t.id === viewport.team)
        ? viewport.team
        : (teams.value[0]?.id ?? null),
  );
  const team = computed(() => teams.value.find((t) => t.id === teamId.value) ?? null);
  /** Who is in the team on screen, by name — who a task can go to and whose
   *  calendar is drawn. */
  const teamMembers = computed((): User[] =>
    (team.value?.memberIds ?? [])
      .map((id) => users.get(id))
      .filter((u): u is User => !!u)
      .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" })),
  );

  /**
   * Whether a task gets the coding tools — repo, branch, Claude runs. A team
   * decides for its tasks; a personal task always has them, since it belongs
   * to no team and its owner may well be coding.
   */
  function isCoding(task: Task): boolean {
    if (task.privateTo !== null) return true;
    return teams.value.find((t) => t.id === task.teamId)?.coding ?? false;
  }

  function setTeams(next: Team[]): void {
    teams.value = [...next].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }

  /** Put another team — or `PERSONAL` — on screen. The thread and calendar
   *  are the team's own, so both start again; the tasks are already here. */
  function switchTeam(id: string): void {
    if (id === PERSONAL) {
      viewport.team = PERSONAL;
      return;
    }
    if (id === teamId.value || !teams.value.some((t) => t.id === id)) return;
    viewport.team = id;
  }

  /** The tmux and Repos views: admins only (the server's rule too — both run
   *  things as `claude`), and only where the board is about code — a coding
   *  team, or Personal, which always is. */
  const tmuxAvailable = computed(
    () => !!me.value?.isAdmin && (personalView.value || !!team.value?.coding),
  );

  /** Whether the reader on this team's admins: the one who may open its Settings. */
  const isTeamAdmin = computed(
    () => !!team.value && !!me.value && (team.value.adminIds.includes(me.value.id) || !!me.value.isAdmin),
  );

  /**
   * Whether the switcher offers `view` on the board on screen. Messages and
   * the calendar are a team's; on Personal there are neither. Shopping is only
   * for a team that keeps a list; Repos, Automation and tmux, for admins on a
   * coding board (`tmuxAvailable`); Settings, for the team's admins. And
   * nothing a team's Settings switched off (`hiddenViews`) — never the board.
   */
  function offersView(view: SwitcherView): boolean {
    if (view === "board") return true;
    if (view === "team") return isTeamAdmin.value;
    if (team.value?.hiddenViews.includes(view)) return false;
    if (view === "tmux" || view === "repos" || view === "automation") return tmuxAvailable.value;
    if (personalView.value && (view === "messages" || view === "calendar")) return false;
    if (view === "shopping") return !personalView.value && !!team.value?.shopping;
    return true;
  }

  // Personal has no messages or calendar; arriving there on one of those
  // views (a switch, or a saved start view) lands on the board instead.
  watch(
    () =>
      (personalView.value && (viewport.view === "messages" || viewport.view === "calendar")) ||
      // The rest are judged once the teams have arrived, or a start view of
      // Shopping never survives the load.
      (loaded.value && (VIEWS as readonly string[]).includes(viewport.view) && !offersView(viewport.view as SwitcherView)),
    (stranded) => {
      if (stranded) viewport.view = "board";
    },
    { immediate: true },
  );

  // Also the first load: the team is only known once the board has arrived.
  watch(teamId, (next, prev) => {
    if (next === prev || !me.value) return;
    messages.value = [];
    messagesReadAt.value = 0;
    messagesLoaded.value = false;
    olderMessages.value = false;
    void loadMessages();
    reloadAvailability();
    shopping.resync();
  });

  // ── derived ────────────────────────────────────────────────────────────
  const kids = computed(() => childrenByParent(tasks.values()));
  const rollups = computed(() => computeRollups(tasks.values()));

  /**
   * Pre-order walk from a set of roots, honouring collapsed nodes. Iterative
   * for the same reason the rollup is: nesting is unbounded by design.
   */
  function walk(roots: Task[], depth0: number, childMap: Map<string | null, Task[]>, rollupMap: Map<string, Rollup>): Row[] {
    const out: Row[] = [];
    const stack: { task: Task; depth: number }[] = [...roots]
      .reverse()
      .map((task) => ({ task, depth: depth0 }));

    while (stack.length) {
      const { task, depth } = stack.pop()!;
      const children = childMap.get(task.id) ?? [];
      const expanded = openTasks.has(task.id);
      // From the unfiltered map: a parent whose children are all hidden is still
      // a parent, and its bar is still a rollup span rather than its own dates.
      const hasChildren = (kids.value.get(task.id)?.length ?? 0) > 0;
      out.push({
        kind: "task",
        key: task.id,
        task,
        depth,
        rollup: rollupMap.get(task.id) ?? EMPTY_ROLLUP,
        hasChildren,
        expanded,
      });
      if (expanded) {
        for (let i = children.length - 1; i >= 0; i--) stack.push({ task: children[i]!, depth: depth + 1 });
      }
    }
    return out;
  }

  /**
   * Every task's place in the board's reading order: a pre-order walk of the
   * whole tree, folded or not, with siblings sorted as the board sorts them. For
   * a list elsewhere that should read like the board (the tmux view's).
   */
  const taskOrder = computed((): Map<string, number> => {
    const childMap = viewport.sortBy === "urgency" ? sortByUrgency(kids.value, rollups.value) : kids.value;
    const out = new Map<string, number>();
    const stack = [...(childMap.get(null) ?? [])].reverse();
    while (stack.length) {
      const task = stack.pop()!;
      out.set(task.id, out.size);
      const children = childMap.get(task.id) ?? [];
      for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]!);
    }
    return out;
  });

  /**
   * The one flat array both panes render.
   *
   * Grouping inserts heading rows and indents everything under them; it does not
   * flatten the tree, because the nesting IS the data model. Roots are filed
   * by repo, and a collapsed heading hides its whole group.
   */
  const visibleRows = computed((): Row[] => {
    // Rollups stay on the full tree: hiding finished work must not make what is
    // left look further along than it is.
    const undone = viewport.hideDone ? pruneDone(kids.value) : kids.value;
    // Every personal task is the reader's — the server sends nobody else's.
    const myId = me.value?.id;
    const filtered = viewport.onlyMine
      ? keepWithAncestors(
          undone,
          // The draft always stays: it is the task being written right now,
          // and it has nobody assigned to it yet by definition.
          (t) => t.privateTo !== null || isDraft(t.id) || (myId !== undefined && t.assignees.includes(myId)),
        )
      : undone;
    const rollupMap = rollups.value;
    const childMap = viewport.sortBy === "urgency" ? sortByUrgency(filtered, rollupMap) : filtered;
    const allRoots = childMap.get(null) ?? [];
    const ctx: GroupCtx = { filed: undone, childMap, rollupMap };
    // The server only ever sends the reader's OWN personal tasks, so any root
    // with an owner is one of theirs. On Personal they are the whole board,
    // filed by repo like a coding team's.
    if (personalView.value) return personalRows(allRoots.filter((t) => t.privateTo !== null), ctx);
    // The board is the team on screen's; the others' trees are held, for a
    // switch that needs no fetch, but not shown.
    const roots = allRoots.filter((t) => t.privateTo === null && t.teamId === teamId.value);

    const out: Row[] = [];
    // A team that does not code has no repos to file by: the plain tree.
    if (team.value && !team.value.coding) {
      out.push(...walk(roots, 0, childMap, rollupMap));
      out.push({ kind: "add", key: "add:end", repo: null, personal: false, depth: 0, rollup: EMPTY_ROLLUP });
      return out;
    }
    for (const [repo, members] of fileBy(roots, undone, (t) => t.repo)) {
      const key = groupKey("repo", repo);
      out.push({ ...heading(key, repo ?? "no repo", null, members, 0, rollupMap), repo });
      if (!openGroups.has(key)) continue;
      out.push(...walk(members, 1, childMap, rollupMap));
      out.push({ kind: "add", key: `add:${key}`, repo, personal: false, depth: 1, rollup: EMPTY_ROLLUP });
    }
    return withAddRow(out);
  });

  interface GroupCtx {
    /** What trees are filed by: before "only mine" cuts them down, so turning
     *  it on never moves a tree to another heading. */
    filed: Map<string | null, Task[]>;
    childMap: Map<string | null, Task[]>;
    rollupMap: Map<string, Rollup>;
  }

  /**
   * Split roots into repo buckets, in the reader's own heading order
   * (`compareRepos`): what they dragged into place, then the rest by name, with
   * the unfiled bucket last, where it reads as a leftover rather than as a repo
   * called nothing.
   */
  function fileBy(
    roots: Task[],
    filed: Map<string | null, Task[]>,
    of: (t: Task) => string | null,
  ): [string | null, Task[]][] {
    const groups = new Map<string | null, Task[]>();
    for (const root of roots) {
      // Filed from the rank-ordered map: which descendant answers first must not
      // depend on the sort, or switching it would move trees between groups.
      const value = dominantValue(root, filed, of);
      const list = groups.get(value);
      if (list) list.push(root);
      else groups.set(value, [root]);
    }
    const byOrder = compareRepos(viewport.repoOrder);
    return [...groups.entries()].sort(([a], [b]) => byOrder(a, b));
  }

  /** Drag one repo heading onto another's place. The order is the reader's own. */
  function moveRepoHeading(from: string | null, to: string | null): void {
    const present = visibleRows.value.flatMap((r) => (r.kind === "group" && r.repo !== undefined ? [r.repo] : []));
    viewport.repoOrder = moveRepo(viewport.repoOrder, present, from, to);
  }

  function heading(
    key: string, label: string, color: string | null, members: Task[], depth: number,
    rollupMap: Map<string, Rollup>,
  ): GroupRowData {
    return {
      kind: "group",
      key,
      label,
      color,
      // Every unfinished task at any depth, not the trees the heading files:
      // "3" means little when one of the three holds forty open sub-tasks.
      count: members.reduce(
        (n, t) => n + (rollupMap.get(t.id)?.openDescendants ?? 0) + (DONE_STATUSES.has(t.status) ? 0 : 1),
        0,
      ),
      rollup: combineRollups(members.map((t) => rollupMap.get(t.id) ?? EMPTY_ROLLUP)),
      expanded: openGroups.has(key),
      depth,
    };
  }

  /**
   * The Personal board: the reader's own tasks, filed by repo. With none yet
   * it is a lone add row, so there is somewhere to add the first one.
   */
  function personalRows(members: Task[], { filed, childMap, rollupMap }: GroupCtx): Row[] {
    const out: Row[] = [];
    const repos = fileBy(members, filed, (t) => t.repo);
    for (const [repo, mine] of repos) {
      const repoKey = personalGroupKey(repo);
      out.push({ ...heading(repoKey, repo ?? "no repo", null, mine, 0, rollupMap), repo, personal: true });
      if (!openGroups.has(repoKey)) continue;
      out.push(...walk(mine, 1, childMap, rollupMap));
      out.push({ kind: "add", key: `add:${repoKey}`, repo, personal: true, depth: 1, rollup: EMPTY_ROLLUP });
    }
    if (repos.length === 0) {
      out.push({ kind: "add", key: "add:personal:", repo: null, personal: true, depth: 0, rollup: EMPTY_ROLLUP });
    }
    return out;
  }

  const selected = computed(() => (selectedId.value ? (tasks.get(selectedId.value) ?? null) : null));

  const userName = (id: string | null): string =>
    id ? (users.get(id)?.displayName ?? users.get(id)?.username ?? "someone") : "system";

  const userColor = (id: string | null): string => (id ? (users.get(id)?.color ?? "#888") : "#888");

  /** What a task would use for repo/branch if it set none of its own. */
  const inherited = (task: Task, key: "repo" | "branch"): string | null =>
    inheritedField(task, key, (id) => tasks.get(id));

  /** The repo/branch a task is actually in: its own override, else inherited. */
  const effective = (task: Task, key: "repo" | "branch"): string | null => task[key] || inherited(task, key);

  // ── event ingestion ────────────────────────────────────────────────────
  function applyEvent(event: ServerEvent): void {
    switch (event.t) {
      case "hello": {
        me.value = event.me;
        users.clear();
        for (const u of event.users) users.set(u.id, u);
        // Joined or left a team while this tab was away: what it holds, and
        // what the resume replays, were both cut for the old membership.
        const before = teams.value.map((t) => t.id).sort().join();
        setTeams(event.teams);
        if (loaded.value && before !== teams.value.map((t) => t.id).sort().join()) resync();
        checkBuild(event.build);
        setDeploying(event.deploying);
        break;
      }
      case "teams": {
        // A team's Settings changed: its name, its views, or who is in it.
        const before = teams.value.map((t) => t.id).sort().join();
        setTeams(event.teams);
        if (loaded.value && before !== teams.value.map((t) => t.id).sort().join()) resync();
        break;
      }
      case "deploying":
        setDeploying(event.deploying);
        break;
      case "snapshot":
        replaceTasks(event.tasks);
        lastSeq.value = event.seq;
        loaded.value = true;
        break;
      case "taskUpsert":
        if (event.opId) pending.delete(event.opId);
        accept(event.task);
        lastSeq.value = Math.max(lastSeq.value, event.seq);
        break;
      case "taskDelete":
        forget(event.id);
        timelines.delete(event.id);
        if (selectedId.value === event.id) select(null);
        lastSeq.value = Math.max(lastSeq.value, event.seq);
        break;
      case "event": {
        const list = timelines.get(event.taskId);
        // Only append to a timeline we have already loaded; otherwise the
        // next open fetches it in full anyway.
        if (list && !list.some((e) => e.id === event.event.id)) list.push(event.event);
        lastSeq.value = Math.max(lastSeq.value, event.seq);
        break;
      }
      case "userUpsert":
        users.set(event.user.id, event.user);
        // A rename from another tab or device.
        if (event.user.id === me.value?.id) me.value = event.user;
        lastSeq.value = Math.max(lastSeq.value, event.seq);
        break;
      case "presence":
        presence.value = event.users;
        break;
      case "message":
        // Another team's thread is fetched when it is switched to.
        if (event.message.teamId === teamId.value && !messages.value.some((m) => m.id === event.message.id)) {
          messages.value.push(event.message);
        }
        lastSeq.value = Math.max(lastSeq.value, event.seq);
        break;
      case "claudeRun":
        // Queued runs (MCP's) are not shown here; the drawer's Claude button is tmux.
        lastSeq.value = Math.max(lastSeq.value, event.seq);
        break;
      case "availabilityUpsert":
        if (event.entry.teamId === teamId.value) availability.set(event.entry.id, event.entry);
        claudeCalendarChanged();
        lastSeq.value = Math.max(lastSeq.value, event.seq);
        break;
      case "availabilityDelete":
        availability.delete(event.id);
        claudeCalendarChanged();
        lastSeq.value = Math.max(lastSeq.value, event.seq);
        break;
      case "shoppingItemUpsert":
      case "shoppingItemDelete":
      case "purchaseUpsert":
      case "purchaseDelete":
        shopping.apply(event);
        lastSeq.value = Math.max(lastSeq.value, event.seq);
        break;
      case "messagesRead":
        if (event.teamId === teamId.value) messagesReadAt.value = Math.max(messagesReadAt.value, event.at);
        break;
      case "ledgerUpsert":
      case "ledgerDelete":
      case "recurringUpsert":
      case "recurringDelete":
      case "financeSettings":
        finance.apply(event);
        break;
      case "gitPush":
        gitPushed(event.push);
        break;
      case "gitGraph":
        gitGraphPushed(event.repo, event.graph);
        break;
      case "runEnded": {
        const { t: _t, ...end } = event;
        runEnded(end);
        break;
      }
      case "deployStatus":
        deployStatusChanged(event.status);
        break;
      case "claudeQueued":
        queuedMoved(event.ids);
        break;
      case "automationFlow":
        flowUpserted(event.flow);
        break;
      case "automationFlowDelete":
        flowRemoved(event.id);
        break;
      case "automationStatus":
        statusesChanged(event.statuses);
        break;
      case "automationLog":
        logsArrived(event.entries);
        break;
      case "automationDashboard":
        widgetChanged(event.flowId, event.nodeId, event.widget);
        break;
      case "reset":
        // Our resume point is older than the retained log; start clean.
        resync();
        break;
    }
  }

  /** Throw away what is held and fetch it again: after a gap too old to
   *  replay, or a change of teams that the replay was not cut for. */
  function resync(): void {
    lastSeq.value = 0;
    void refresh();
    void loadMessages();
    reloadAvailability();
    claudeCalendarChanged();
  }

  /**
   * Fetch the calendar window again from scratch. Entries held from before a
   * gap may have been deleted meanwhile, and a delete leaves no trace to
   * replay; and another team's calendar shares none of them.
   */
  function reloadAvailability(): void {
    const held = availabilityWindow.value;
    availability.clear();
    availabilityWindow.value = null;
    if (held) void loadAvailability(held.startMs, held.endMs);
  }

  /** A fetched board with what this tab heard while it was in flight laid over
   *  it: a row set since (`before` held another, or none) wins unless the fetch
   *  has a newer version, and one deleted since stays gone. */
  function sinceFetch(list: Task[], before: ReadonlyMap<string, Task>): Task[] {
    const byId = new Map(list.map((t) => [t.id, t]));
    for (const id of before.keys()) if (id !== DRAFT_ID && !tasks.has(id)) byId.delete(id);
    for (const [id, row] of tasks) {
      if (id === DRAFT_ID || before.get(id) === row) continue;
      const fetched = byId.get(id);
      if (!fetched || fetched.version < row.version) byId.set(id, confirmed.get(id) ?? row);
    }
    return [...byId.values()];
  }

  /** Swap the board for a freshly fetched or replayed one, keeping the draft:
   *  a half-typed task in front of somebody is not the server's to take away,
   *  and the server has never heard of it to send it back. */
  function replaceTasks(list: Task[]): void {
    const held = tasks.get(DRAFT_ID);
    tasks.clear();
    confirmed.clear();
    for (const t of list) tasks.set(t.id, t);
    for (const { id } of pending.values()) {
      const row = tasks.get(id);
      if (row) confirmed.set(id, row);
    }
    for (const id of confirmed.keys()) redraw(id);
    if (held) tasks.set(DRAFT_ID, held);
    // Deleted while this tab was not listening: nothing will say so again. Not
    // on the first load, which a deep link's selection waits for.
    if (loaded.value && selectedId.value && !tasks.has(selectedId.value)) select(null);
  }

  /**
   * A row from the server — a broadcast, or the reply to a write. Never one
   * older than what is held: the reply to alice's write can land after the
   * broadcast of bob's that followed it, and would put his change out of sight.
   * Anything this tab still has pending on the task is drawn over it.
   */
  function accept(row: Task): void {
    const known = confirmed.get(row.id) ?? tasks.get(row.id);
    if (known && known.version > row.version) return;
    if (![...pending.values()].some((op) => op.id === row.id)) {
      confirmed.delete(row.id);
      tasks.set(row.id, row);
      return;
    }
    confirmed.set(row.id, row);
    redraw(row.id);
  }

  /** A reply about a task that may have been deleted while it was in flight:
   *  the delete is the newer word, so the row stays gone. */
  function acceptReply(row: Task): void {
    if (tasks.has(row.id)) accept(row);
  }

  function forget(id: string): void {
    tasks.delete(id);
    confirmed.delete(id);
    for (const [key, op] of pending) if (op.id === id) pending.delete(key);
  }

  /** The server's row with this tab's pending changes over it, in the order made. */
  function redraw(id: string): void {
    const base = confirmed.get(id);
    if (!base) return;
    let row = base;
    let any = false;
    for (const op of pending.values()) {
      if (op.id !== id) continue;
      row = { ...row, ...op.patch } as Task;
      any = true;
    }
    tasks.set(id, row);
    if (!any) confirmed.delete(id);
  }

  /** Start drawing a pending change; the task's current row is the server's
   *  word until a newer one comes. */
  function hold(key: string, id: string, patch: Partial<Task>): void {
    const row = tasks.get(id);
    if (!row) return;
    if (!confirmed.has(id)) confirmed.set(id, toRaw(row));
    pending.delete(key); // re-set moves it last: the newest preview wins
    pending.set(key, { id, patch });
    redraw(id);
  }

  function letGo(key: string, id: string): void {
    pending.delete(key);
    redraw(id);
  }

  /**
   * Draw a change nothing has been sent for — a bar or a slider being dragged —
   * and keep drawing it whatever arrives meanwhile. `endPreview` takes it away
   * again; the write, if there is one, is an ordinary `patchTask` after it.
   */
  function previewTask(id: string, patch: Partial<Task>): void {
    hold(`preview:${id}`, id, patch);
  }

  function endPreview(id: string): void {
    letGo(`preview:${id}`, id);
  }

  async function refresh(): Promise<void> {
    // What the store held as the fetch went out: a row that changed, arrived or
    // went while it was in flight is newer than the reply, which the server may
    // have read before it — on a slow line, a task made meanwhile would vanish.
    const before = new Map(tasks);
    const { tasks: list, users: people, teams: mine } = await api.get<{ tasks: Task[]; users: User[]; teams: Team[] }>(
      "/api/board",
    );
    replaceTasks(sinceFetch(list, before));
    users.clear();
    for (const u of people) users.set(u.id, u);
    setTeams(mine);
    loaded.value = true;
  }

  async function start(user: User, settings: UserSettings): Promise<void> {
    me.value = user;
    viewport.applySettings(settings);
    // Messages follow: the `teamId` watch loads them once the teams are known.
    await refresh();
    socket?.close();
    let opened = false;
    let resyncTimer: ReturnType<typeof setTimeout> | null = null;
    const resync = (): void => {
      finance.resync();
      shopping.resync();
      if (me.value?.isAdmin) {
        void loadTaskPromptTemplate();
        void loadQueuedTasks();
        automationReconnected();
      }
    };
    socket = connect({
      onEvent: applyEvent,
      onStatus: (s) => (status.value = s),
      lastSeq: () => lastSeq.value,
      onOpen: (say) => {
        // The socket's own resume goes at once. The reads after a REconnect wait
        // 0–2s at random: a restarted server has every tab back at the same
        // moment, and their GETs all at once are what keeps it slow.
        if (resyncTimer) clearTimeout(resyncTimer);
        if (!opened) resync();
        else resyncTimer = setTimeout(() => ((resyncTimer = null), resync()), Math.random() * RESYNC_JITTER_MS);
        opened = true;
        say({ t: "viewing", taskId: selectedId.value });
        if (myLock.value) say({ t: "editing", lock: myLock.value });
      },
    });
  }

  function stop(): void {
    socket?.close();
    socket = null;
    tasks.clear();
    users.clear();
    teams.value = [];
    timelines.clear();
    availability.clear();
    availabilityWindow.value = null;
    finance.reset();
    shopping.reset();
    signedOutAutomation();
    forgetQueuedTasks();
    draft.value = null;
    me.value = null;
    myLock.value = null;
    messages.value = [];
    messagesReadAt.value = 0;
    messagesLoaded.value = false;
    lastSeq.value = 0;
    loaded.value = false;
  }

  function send(msg: ClientMsg): void {
    socket?.send(msg);
  }

  // ── mutations (REST; the echo arrives over the socket) ──────────────────
  /**
   * Start a task. NOTHING is sent: the row that appears is the draft (see
   * DRAFT_ID), and naming it is what creates it. Thinking better of it — the
   * drawer closed, another row clicked, "Discard" — leaves no trace anywhere.
   *
   * It is returned so the caller can select and reveal it exactly as it did
   * when this was a POST; the id it carries is the local one until it saves.
   */
  function addTask(input: {
    parentId?: string | null;
    afterId?: string | null;
    repo?: string | null;
    personal?: boolean;
    /** A root's team, when it is not the board on screen's: a sibling added
     *  from the drawer over the tmux view, which lists every board's tasks. */
    teamId?: string | null;
  }): Task {
    discardDraft();
    // A new root goes on the board on screen — a team's, or Personal. A
    // sub-task is whatever its tree is, which the server decides from the
    // parent; mirrored here so the draft row files itself the same way.
    const personal = input.personal ?? (!input.parentId && personalView.value);
    const parent = input.parentId ? (tasks.get(input.parentId) ?? null) : null;
    const rootTeam = input.teamId ?? teamId.value;
    const now = Date.now();
    const task: Task = {
      id: DRAFT_ID,
      parentId: input.parentId ?? null,
      teamId: parent ? parent.teamId : personal ? null : rootTeam,
      privateTo: parent ? parent.privateTo : personal ? (me.value?.id ?? null) : null,
      rank: draftRank(input.parentId ?? null, input.afterId),
      title: "",
      notes: "",
      status: "todo",
      startMs: null,
      endMs: null,
      estHours: DEFAULT_EST_HOURS,
      progress: null,
      urgency: URGENCY_DEFAULT,
      time: [],
      repo: input.repo ?? null,
      branch: null,
      claudeHidden: false,
      adminTask: false,
      skipReview: false,
      assignees: [],
      blockedBy: [],
      createdBy: me.value?.id ?? "",
      createdAt: now,
      updatedAt: now,
      version: 0,
    };
    const placement: DraftPlacement = input.parentId || personal
      ? { parentId: input.parentId ?? null, personal }
      : { teamId: rootTeam };
    // Three meanings, and the third is the absence of the key: an id = after
    // that sibling, null = first, omitted = last.
    if ("afterId" in input) placement.afterId = input.afterId;
    draft.value = { placement, saving: false };
    tasks.set(DRAFT_ID, task);
    return task;
  }

  /** Where the draft row sits among its siblings, worked out the way the
   *  server would (`rankForSlot`), so it does not jump when the real row
   *  arrives in its place. */
  function draftRank(parentId: string | null, afterId?: string | null): string {
    const siblings = (kids.value.get(parentId) ?? []).filter((t) => t.id !== DRAFT_ID);
    const above = afterId === undefined
      ? siblings.length - 1
      : afterId === null
        ? -1
        : siblings.findIndex((t) => t.id === afterId);
    return rankBetween(siblings[above]?.rank ?? null, siblings[above + 1]?.rank ?? null);
  }

  const isDraft = (id: string | null): boolean => id === DRAFT_ID;

  /**
   * A write to the draft: fold it in, and — once there is a title to save it
   * under — create it for real, carrying everything set on it meanwhile.
   *
   * Until then nothing goes out. An empty title is not a task the server would
   * take (it requires one), and a blank row on everyone's board is what the
   * draft exists to avoid, so a change of urgency before the name simply waits
   * for the name.
   */
  async function commitDraft(patch: Partial<Task>): Promise<void> {
    const held = draft.value;
    const current = tasks.get(DRAFT_ID);
    if (!held || !current) return;
    const next = { ...current, ...patch } as Task;
    tasks.set(DRAFT_ID, next);
    const title = (next.title ?? "").trim();
    if (!title) return;

    // Set before the first await: a click elsewhere now must not throw away a
    // task that is already on its way to the server.
    held.saving = true;
    try {
      const { task } = await api.post<{ task: Task }>("/api/tasks", {
        ...held.placement,
        title,
        notes: next.notes,
        status: next.status,
        estHours: next.estHours,
        progress: next.progress,
        urgency: next.urgency,
        repo: next.repo,
        branch: next.branch,
        claudeHidden: next.claudeHidden,
        adminTask: next.adminTask,
        skipReview: next.skipReview,
        startMs: next.startMs,
        endMs: next.endMs,
      });
      draft.value = null;
      tasks.delete(DRAFT_ID);
      accept(task);
      // The drawer, the URL and the tree were all on the draft: move them onto
      // the row the server made, replacing the history entry rather than
      // adding one (see lib/taskRoute.ts). Without opening the parent: a row
      // added under a folded one stays folded away, named or not.
      if (selectedId.value === DRAFT_ID) select(task.id, { reveal: false });
      // Not part of the create route: whoever was picked while it was a draft.
      if (next.assignees.length) await patchTask(task.id, { assignees: next.assignees });
    } catch (err) {
      held.saving = false;
      throw err;
    }
  }

  /**
   * A task made in one go, title and repo known up front — the repo
   * switcher's "+" and the feedback button. No draft: there is nothing left to fill in before it is worth
   * sending. It goes last among the repo's trees on the board on screen, a
   * root of its own.
   */
  async function quickTask(title: string, repo: string, notes = "", urgency?: number): Promise<Task> {
    const placement: DraftPlacement = personalView.value ? { parentId: null, personal: true } : { teamId: teamId.value };
    const { task } = await api.post<{ task: Task }>("/api/tasks", {
      ...placement, title, repo, ...(notes ? { notes } : {}), ...(urgency === undefined ? {} : { urgency }),
    });
    tasks.set(task.id, task);
    return task;
  }

  /** The feedback button's: a task made and queued for Claude by the server
   *  (server/feedback.ts), on the board on screen like a quick task. */
  async function feedbackTask(text: string, url: string, repo: string, title = "", fields: Record<string, string> = {}): Promise<Task> {
    const placement: DraftPlacement = personalView.value ? { parentId: null, personal: true } : { teamId: teamId.value };
    const { task } = await api.post<{ task: Task }>("/api/feedback", { ...placement, text, url, repo, title, fields });
    accept(task);
    return task;
  }

  /** Whether a task is on the board on screen. `tasks` holds every team's
   *  trees and the reader's personal ones, for a switch that needs no fetch,
   *  so anything listing "the board's" tasks has to ask. The server only ever
   *  sends the reader's own personal tasks, so any with an owner is theirs. */
  function onBoard(t: Task): boolean {
    return personalView.value ? t.privateTo !== null : t.privateTo === null && t.teamId === teamId.value;
  }

  /** Throw away an unsaved draft. One whose create is in flight is no longer
   *  ours to drop — the echo puts the real row on the board. */
  function discardDraft(): void {
    if (!draft.value || draft.value.saving) return;
    draft.value = null;
    tasks.delete(DRAFT_ID);
  }

  /**
   * Take a shared task and its subtree into the reader's personal tasks. Not
   * optimistic: it is refused while anyone else is still on the work, and the
   * sub-tasks arrive over the socket either way.
   */
  async function makePersonal(id: string, dest: { parentId?: string | null; afterId?: string | null } = {}): Promise<void> {
    const { task } = await api.post<{ task: Task }>(`/api/tasks/${id}/personal`, dest);
    accept(task);
    // It has left the team's board: follow it to Personal, where it now is.
    switchTeam(PERSONAL);
    if (task.parentId) reveal(task.id);
  }

  /**
   * The way back: one of the reader's personal tasks, and its subtree, onto a
   * team's board. Not optimistic either — a blocker the team cannot see
   * refuses it — and the board follows it to that team.
   */
  async function makeShared(id: string, dest: { teamId: string; parentId?: string | null; afterId?: string | null }): Promise<void> {
    const { task } = await api.post<{ task: Task }>(`/api/tasks/${id}/share`, dest);
    accept(task);
    switchTeam(dest.teamId);
    if (task.parentId) reveal(task.id);
  }

  /**
   * Optimistic patch: drawn at once, over anything the server sends about the
   * task meanwhile, until the server answers. On failure what is left is the
   * server's latest row — which keeps anyone else's change made in the meantime.
   */
  async function patchTask(id: string, patch: Partial<Task> & { afterId?: string | null }): Promise<void> {
    // Nothing exists to patch yet: the change goes into the draft, which saves
    // itself as soon as it has a title.
    if (isDraft(id)) return commitDraft(patch);
    if (!tasks.has(id)) return;
    const opId = `${id}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    hold(opId, id, patch);
    try {
      const { task } = await api.patch<{ task: Task }>(`/api/tasks/${id}`, { ...patch, opId });
      pending.delete(opId);
      acceptReply(task);
    } finally {
      // Refused: what is left is the server's latest row. Answered: a reply
      // older than a broadcast already held was not taken, and this draws that.
      letGo(opId, id);
    }
  }

  /** Moving a task to doing makes it yours; the server does that. */
  async function setStatus(id: string, next: Status): Promise<void> {
    await patchTask(id, { status: next });
  }

  /** A change the SERVER is about to make on its own — starting Claude on a task
   *  puts it in doing — drawn now, with nothing sent. The server's upsert
   *  replaces the row when it lands; until then, the returned function (the
   *  request failed) or the timeout puts the row back, but only while it is
   *  still this guess: anything newer is somebody's word, not ours. */
  function guessTask(id: string, patch: Partial<Task>, ms = 30_000): () => void {
    const before = tasks.get(id);
    if (!before || Object.entries(patch).every(([k, v]) => (before as unknown as Record<string, unknown>)[k] === v)) return () => {};
    const was = toRaw(before);
    const guessed = { ...was, ...patch } as Task;
    tasks.set(id, guessed);
    const revert = (): void => {
      clearTimeout(timer);
      if (toRaw(tasks.get(id)) === guessed) tasks.set(id, was);
    };
    const timer = setTimeout(revert, ms);
    return revert;
  }

  /** Put a task in doing, unless it already is — what starting Claude on it
   *  says. Claude's time is read from its tmux session, so no clock starts. */
  async function startWork(id: string): Promise<void> {
    if (tasks.get(id)?.status !== "doing") await patchTask(id, { status: "doing" });
  }

  /** Log hours you worked on top of your own total; negative takes some back.
   *  The server narrates it on the timeline. */
  async function logHours(id: string, hours: number): Promise<void> {
    const { task } = await api.post<{ task: Task }>(`/api/tasks/${id}/time`, { add: hours });
    acceptReply(task);
  }

  async function removeTask(id: string): Promise<void> {
    // A draft was never sent, so dropping it is the whole delete. Selecting
    // nothing discards it and closes the drawer in one go.
    if (isDraft(id)) {
      if (selectedId.value === DRAFT_ID) select(null);
      else discardDraft();
      return;
    }
    // Gone from the screen at once, with everything under it, as the server will
    // take it; all of it put back if the delete is refused.
    const under = new Map<string, string[]>();
    for (const t of tasks.values()) if (t.parentId) under.set(t.parentId, [...(under.get(t.parentId) ?? []), t.id]);
    const taken: Task[] = [];
    for (let next = [id]; next.length; ) {
      const at = next.pop()!;
      const row = tasks.get(at);
      if (row) taken.push(toRaw(row));
      next.push(...(under.get(at) ?? []));
    }
    for (const row of taken) tasks.delete(row.id);
    try {
      const { deleted } = await api.del<{ deleted: string[] }>(`/api/tasks/${id}`);
      for (const gone of deleted) forget(gone);
    } catch (err) {
      for (const row of taken) if (!tasks.has(row.id)) tasks.set(row.id, row);
      throw err;
    }
  }

  async function loadTimeline(id: string): Promise<void> {
    // A draft has no history, and the server has never heard of it.
    if (isDraft(id)) return;
    const { events } = await api.get<{ events: TaskEvent[] }>(`/api/tasks/${id}/events`);
    timelines.set(id, events);
  }

  async function comment(id: string, body: string): Promise<void> {
    const { event } = await api.post<{ event: TaskEvent }>(`/api/tasks/${id}/comments`, { body });
    const list = timelines.get(id);
    if (list && !list.some((e) => e.id === event.id)) list.push(event);
  }

  // ── availability ───────────────────────────────────────────────────────
  /** One span of the team on screen's calendar. Entries arriving after a switch
   *  away are dropped rather than mixed into the other team's. */
  async function fetchAvailability(span: { startMs: number; endMs: number }): Promise<Availability[]> {
    const team = teamId.value;
    if (!team) return [];
    const { availability: list } = await api.get<{ availability: Availability[] }>(
      `/api/availability?from=${span.startMs}&to=${span.endMs}&team=${encodeURIComponent(team)}`,
    );
    return list.filter((e) => e.teamId === teamId.value);
  }

  /**
   * Make sure a span is loaded, fetching only what is not held already.
   *
   * The calendar scrolls endlessly and asks for a slightly wider range every
   * time it grows, so this has to be incremental: refetching the union each
   * time would mean the twentieth screenful costs twenty times the first. Only
   * the gaps either side of what is held are requested, and the held window
   * becomes the union.
   *
   * Entries already held are refreshed rather than duplicated — they are keyed
   * by id — and one straddling a boundary simply arrives twice.
   */
  async function loadAvailability(startMs: number, endMs: number): Promise<void> {
    const held = availabilityWindow.value;
    if (held && startMs >= held.startMs && endMs <= held.endMs) return;

    if (!held) {
      // The first load owns the whole window, so it replaces rather than
      // merges: whatever is in the map predates this calendar being opened.
      const entries = await fetchAvailability({ startMs, endMs });
      availability.clear();
      for (const entry of entries) availability.set(entry.id, entry);
      availabilityWindow.value = { startMs, endMs };
      return;
    }

    const gaps: { startMs: number; endMs: number }[] = [];
    if (startMs < held.startMs) gaps.push({ startMs, endMs: held.startMs });
    if (endMs > held.endMs) gaps.push({ startMs: held.endMs, endMs });
    const pages = await Promise.all(gaps.map(fetchAvailability));
    for (const entry of pages.flat()) availability.set(entry.id, entry);
    availabilityWindow.value = {
      startMs: Math.min(startMs, held.startMs),
      endMs: Math.max(endMs, held.endMs),
    };
  }

  /** Add a span to a calendar — your own, or anyone's if you are an admin. */
  async function addAvailability(input: AvailabilityInput & { userId?: string }): Promise<Availability> {
    const { entry } = await api.post<{ entry: Availability }>("/api/availability", { ...input, teamId: teamId.value });
    availability.set(entry.id, entry);
    return entry;
  }

  async function patchAvailability(id: string, patch: Partial<AvailabilityInput>): Promise<Availability> {
    const { entry } = await api.patch<{ entry: Availability }>(`/api/availability/${id}`, patch);
    availability.set(entry.id, entry);
    return entry;
  }

  async function removeAvailability(id: string): Promise<void> {
    await api.del(`/api/availability/${id}`);
    availability.delete(id);
  }

  /** Whether this person may write in `userId`'s calendar. Mirrors the server's rule. */
  const mayEditCalendar = (userId: string): boolean => me.value?.id === userId || me.value?.isAdmin === true;

  // ── message board ──────────────────────────────────────────────────────
  /** Messages from other people newer than the read marker. Your own never count. */
  const unreadMessages = computed(
    () => messages.value.filter((m) => m.userId !== me.value?.id && m.createdAt > messagesReadAt.value).length,
  );

  /** The newest page. Replaces what is held: called on start and after a reset,
   *  when anything older we had may no longer line up with the server. */
  async function loadMessages(): Promise<void> {
    const team = teamId.value;
    if (!team) {
      messagesLoaded.value = true;
      return;
    }
    const page = await api.get<{ messages: Message[]; hasMore: boolean; lastReadAt: number }>(
      `/api/messages?team=${encodeURIComponent(team)}`,
    );
    // Switched away while it was in flight: the other team's load owns the list now.
    if (team !== teamId.value) return;
    messages.value = page.messages;
    olderMessages.value = page.hasMore;
    messagesReadAt.value = Math.max(messagesReadAt.value, page.lastReadAt);
    messagesLoaded.value = true;
  }

  /** The page before the oldest message held, prepended. */
  async function loadOlderMessages(): Promise<void> {
    const oldest = messages.value[0];
    if (!oldest || !olderMessages.value) return;
    const team = teamId.value;
    const page = await api.get<{ messages: Message[]; hasMore: boolean }>(
      `/api/messages?before=${encodeURIComponent(oldest.id)}&team=${encodeURIComponent(team ?? "")}`,
    );
    if (team !== teamId.value) return;
    const held = new Set(messages.value.map((m) => m.id));
    messages.value = [...page.messages.filter((m) => !held.has(m.id)), ...messages.value];
    olderMessages.value = page.hasMore;
  }

  async function postMessage(body: string): Promise<void> {
    const { message } = await api.post<{ message: Message }>("/api/messages", { body, teamId: teamId.value });
    if (!messages.value.some((m) => m.id === message.id)) messages.value.push(message);
    messagesReadAt.value = Math.max(messagesReadAt.value, message.createdAt);
  }

  /** Everything up to the newest message held has been seen. Skipped when there
   *  is nothing new, so an open panel does not post on every render. */
  async function markMessagesRead(): Promise<void> {
    const newest = messages.value.at(-1);
    if (!newest || newest.createdAt <= messagesReadAt.value) return;
    messagesReadAt.value = newest.createdAt;
    await api.post("/api/messages/read", { at: newest.createdAt, teamId: newest.teamId });
  }

  // ── field locks ────────────────────────────────────────────────────────
  /**
   * Someone else who is typing in this field right now, or null. Your own locks
   * never count — a second tab of yours is still you.
   */
  function lockHolder(taskId: string, field: EditableField): User | null {
    const holder = presence.value.find(
      (p) => p.userId !== me.value?.id && p.editing?.taskId === taskId && p.editing.field === field,
    );
    return holder ? (users.get(holder.userId) ?? null) : null;
  }

  /** Focus a field: take its lock. The server refuses if someone beat us to it,
   *  and the presence broadcast then shows the field locked to them. */
  function claim(taskId: string, field: EditableField): void {
    // A draft is nobody else's to walk into, and the server has no row to
    // hang the lock on.
    if (isDraft(taskId)) return;
    // Called on every keystroke too; only say it once.
    if (myLock.value?.taskId === taskId && myLock.value.field === field) return;
    myLock.value = { taskId, field };
    send({ t: "editing", lock: myLock.value });
  }

  /** Blur a field: let go, unless focus has already moved on to another one. */
  function release(taskId: string, field: EditableField): void {
    if (myLock.value?.taskId !== taskId || myLock.value.field !== field) return;
    myLock.value = null;
    send({ t: "editing", lock: null });
  }

  function select(id: string | null, opts: { reveal?: boolean } = {}): void {
    // Moving off an unsaved draft is deciding against it: it goes, unsent.
    if (!isDraft(id)) discardDraft();
    // The server drops the lock when viewing moves on; match it here.
    if (myLock.value && myLock.value.taskId !== id) myLock.value = null;
    // A link to another team's task (a notification, a deep link) brings its
    // team on screen, or the drawer would open over a board it is not on.
    const target = id ? tasks.get(id) : undefined;
    if (target) switchTeam(target.privateTo !== null ? PERSONAL : target.teamId!);
    // Not a draft, though: adding a sub-task never unfolds its parent. The
    // drawer is on the new row, and the tree stays as the reader left it.
    if (id && !isDraft(id) && opts.reveal !== false) reveal(id);
    selectedId.value = id;
    // Nobody else can be looking at a task that does not exist yet.
    send({ t: "viewing", taskId: isDraft(id) ? null : id });
    if (id && !timelines.has(id)) void loadTimeline(id);
  }

  /**
   * Open everything ABOVE a task, so the row is in the tree when the drawer
   * closes. Not the task itself: which of its children you want open is yours
   * to say, and unfolding a subtree because somebody clicked its heading is the
   * wall of rows the closed default exists to avoid.
   *
   * Every way into the drawer needs this now that a board arrives folded — a
   * deep link (`?task=`), the parent and child links in the drawer, and a
   * selection restored by the back button all land on rows nothing on screen
   * leads to.
   */
  function reveal(id: string): void {
    let root = tasks.get(id);
    let parentId = root?.parentId ?? null;
    const seen = new Set<string>(); // a cycle is impossible, but this walk is not the place to find out
    while (parentId && !seen.has(parentId)) {
      seen.add(parentId);
      openTasks.add(parentId);
      root = tasks.get(parentId) ?? root;
      parentId = tasks.get(parentId)?.parentId ?? null;
    }
    if (!root) return;
    // Filed as `visibleRows` files it, so the heading opened is the one it is under.
    const filed = viewport.hideDone ? pruneDone(kids.value) : kids.value;
    const repo = dominantValue(root, filed, (t) => t.repo);
    openGroups.add(root.privateTo !== null ? personalGroupKey(repo) : groupKey("repo", repo));
  }

  // Rows open folded, so "only mine" alone would show a column of parents with
  // the reader's tasks shut inside them. Turning it on opens the way to each.
  watch(
    () => viewport.onlyMine && loaded.value,
    (on) => {
      const myId = me.value?.id;
      if (!on || !myId) return;
      for (const t of tasks.values()) if (t.assignees.includes(myId) || t.privateTo !== null) reveal(t.id);
    },
    { immediate: true },
  );

  /** One twisty handler for both kinds of row, each read the way its own set
   *  means it. */
  function toggle(key: string): void {
    const set = isGroupKey(key) ? openGroups : openTasks;
    if (set.has(key)) set.delete(key);
    else set.add(key);
  }

  return {
    tasks, users, teams, teamId, team, teamMembers, personalView, isTeamAdmin, offersView, onBoard, switchTeam, isCoding, tmuxAvailable, timelines, presence, openTasks, openGroups, me, status, selectedId, loaded, lastSeq,
    kids, rollups, visibleRows, taskOrder, selected,
    userName, userColor, inherited, effective,
    start, stop, refresh, applyEvent, send,
    addTask, quickTask, feedbackTask, isDraft, discardDraft, patchTask, previewTask, endPreview, guessTask, makePersonal, makeShared, moveRepoHeading, setStatus, startWork, logHours, removeTask, comment, loadTimeline, select, toggle, reveal,
    lockHolder, claim, release,
    messages, olderMessages, messagesReadAt, messagesLoaded, unreadMessages,
    loadMessages, loadOlderMessages, postMessage, markMessagesRead,
    availability, availabilityWindow, loadAvailability, addAvailability, patchAvailability, removeAvailability, mayEditCalendar,
  };
});
